import axios from 'axios'
import tokenService from './token'
import { createApiUrl, LONG_REQUEST_TIMEOUT_MS } from '../config/api'

const chatbotUrl = createApiUrl('/api/automation/chatbot/')
const chatbotStreamUrl = createApiUrl('/api/automation/chatbot/stream/')

// Non-streaming fallback. Resolves with { reply, conversation_id, study_plan, resources }.
// A multi-step LLM pipeline can plausibly exceed the global default timeout (issue #407).
const sendMessage = async ({ message, history = [], conversationId = null }) => {
    const res = await axios.post(
        chatbotUrl,
        { message, history, conversation_id: conversationId },
        { ...tokenService.getAuthHeader(), timeout: LONG_REQUEST_TIMEOUT_MS }
    )
    return res.data
}

// Streaming variant (Server-Sent Events). Calls `onEvent(event)` for each parsed event as the
// backend narrates the pipeline: several { type: 'status', stage, message, detail } events, then a
// terminal { type: 'done', payload } (or { type: 'error', message }). Uses fetch + a ReadableStream
// because EventSource can't POST a body / send auth headers.
const streamMessage = async ({ message, history = [], conversationId = null, onEvent, signal }) => {
    const auth = tokenService.getAuthHeader() || {}
    const res = await fetch(chatbotStreamUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth.headers || {}) },
        body: JSON.stringify({ message, history, conversation_id: conversationId }),
        signal,
    })
    if (!res.ok || !res.body) {
        throw new Error(`Chatbot stream failed (${res.status})`)
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    // SSE frames are separated by a blank line; each frame's payload is a `data:` line.
    for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let sep
        while ((sep = buffer.indexOf('\n\n')) >= 0) {
            const frame = buffer.slice(0, sep).trim()
            buffer = buffer.slice(sep + 2)
            if (frame.startsWith('data:')) {
                try {
                    onEvent(JSON.parse(frame.slice(5).trim()))
                } catch (e) {
                    // ignore malformed frame
                }
            }
        }
    }
}

const chatbotService = {
    sendMessage,
    streamMessage,
}

export default chatbotService
